import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import {
  Button,
  CircularProgress,
  Grid,
  Box,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CustomDialog from "./Dialog";
import ProductCardSkeleton from "./ProductCardSkeleton";
import useProducts from "../hooks/useProducts";
import useBookmarks from "../hooks/useBookmarks";
import useImagePrefetch, { prefetchImages } from "../hooks/useImagePrefetch";
import { useQueryClient } from "@tanstack/react-query";
import { qk } from "../api/queryKeys";
import { getProductById, getProductMedia } from "../api/endpoints";

const SKELETON_COUNT = 8;

function Products({
  selectedCategory,
  selectedFilters,
  selectedSort = "",
  clearFilters,
  setSelectedCategory = () => {},
  onProductsCountChange = () => {},
  onCategoryBannerChange = () => {},
  onCategoryNameChange = () => {},
}) {
  const navigate = useNavigate();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const { isBookmarked, toggle: toggleBookmark } = useBookmarks();

  const location = useLocation();
  const searchParams = new URLSearchParams(location?.search);
  const keyParam = searchParams.get("key");

  const filtersForQuery = useMemo(() => {
    if (
      selectedFilters &&
      typeof selectedFilters === "object" &&
      Object.keys(selectedFilters).length > 0
    ) {
      return selectedFilters;
    }
    return {};
  }, [selectedFilters]);

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useProducts({
    category: selectedCategory,
    filters: filtersForQuery,
    sort: selectedSort,
  });

  const products = useMemo(
    () => data?.pages?.flatMap((p) => p?.products || []) || [],
    [data]
  );
  const firstPage = data?.pages?.[0];

  const imageUrls = useMemo(
    () => products.map((p) => p?.featured_image).filter(Boolean),
    [products]
  );
  useImagePrefetch(imageUrls);

  useEffect(() => {
    onProductsCountChange(products.length);
  }, [products.length, onProductsCountChange]);

  useEffect(() => {
    if (firstPage) {
      onCategoryBannerChange(firstPage.category_featured_image || "");
      onCategoryNameChange(firstPage.category_name || "");
    }
  }, [firstPage, onCategoryBannerChange, onCategoryNameChange]);

  const handleOpenDialog = (product) => {
    setSelectedProduct(product);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleProductClick = (productId) => {
    navigate(`/products/${productId}`, {
      state: { category: selectedCategory },
    });
  };

  const queryClient = useQueryClient();

  const handleProductHover = (product) => {
    if (!product?.post_id) return;
    const id = product.post_id;
    if (product.featured_image) prefetchImages([product.featured_image]);

    queryClient
      .prefetchQuery({
        queryKey: qk.products.detail(id),
        queryFn: ({ signal }) => getProductById(id, { signal }),
        staleTime: 5 * 60 * 1000,
      })
      .then(() => {
        const detail = queryClient.getQueryData(qk.products.detail(id));
        const related = detail?.products?.[0]?.suggested_pairings || [];
        prefetchImages(related.map((p) => p?.featured_image).filter(Boolean));
      })
      .catch(() => {});

    queryClient
      .prefetchQuery({
        queryKey: qk.products.media(id),
        queryFn: ({ signal }) => getProductMedia(id, { signal }),
        staleTime: 5 * 60 * 1000,
      })
      .then(() => {
        const media = queryClient.getQueryData(qk.products.media(id));
        if (media?.gallery_images) prefetchImages(media.gallery_images);
      })
      .catch(() => {});
  };

  // Clear filters when the user switches category, but skip the very first
  // run so persisted filters survive an initial mount/back-navigation.
  const didMountForCategoryRef = useRef(false);
  useEffect(() => {
    if (!didMountForCategoryRef.current) {
      didMountForCategoryRef.current = true;
      return;
    }
    clearFilters();
  }, [selectedCategory]);

  if (isLoading) {
    return (
      <Grid container spacing={3}>
        {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
          <Grid
            item
            key={`skeleton-${i}`}
            xs={12}
            sm={6}
            md={3}
            sx={{ marginBottom: "20px" }}>
            <ProductCardSkeleton />
          </Grid>
        ))}
      </Grid>
    );
  }

  if (isError) {
    return (
      <Stack
        justifyContent="center"
        alignItems="center"
        spacing={2}
        sx={{ py: 6 }}>
        <Typography sx={{ color: "#7D7D7D" }}>
          {error?.message || "Failed to load products."}
        </Typography>
        <Button
          variant="contained"
          onClick={() => refetch()}
          sx={{
            backgroundColor: "#333",
            "&:hover": { backgroundColor: "#333" },
          }}>
          Retry
        </Button>
      </Stack>
    );
  }

  return (
    <>
      {products.length > 0 ? (
        <Grid container spacing={3}>
          {products.map((product, index) => (
            <Grid
              item
              key={product?.post_id || index}
              xs={12}
              sm={6}
              md={3}
              sx={{ marginBottom: "20px" }}>
              <Box
                sx={{ position: "relative", cursor: "pointer" }}
                onClick={() => handleProductClick(product?.post_id)}
                onMouseEnter={() => handleProductHover(product)}
                onTouchStart={() => handleProductHover(product)}>
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleBookmark(product);
                  }}
                  sx={{
                    position: "absolute",
                    top: "20px",
                    right: "20px",
                    zIndex: 10,
                    cursor: "pointer",
                    backgroundColor: isBookmarked(product?.post_id)
                      ? "#323232"
                      : "#fff",
                    borderRadius: "50%",
                    padding: "6px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    transition: "transform 0.2s ease",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.transform = "scale(1.1)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.transform = "scale(1)")
                  }>
                  {isBookmarked(product?.post_id) ? (
                    <BookmarkBorderIcon sx={{ color: "#fff", fontSize: 24 }} />
                  ) : (
                    <BookmarkBorderIcon
                      sx={{ color: "#323232", fontSize: 24 }}
                    />
                  )}
                </Box>
                <Box
                  sx={{
                    width: "100%",
                    aspectRatio: "1 / 1",
                    overflow: "hidden",
                    mb: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    position: "relative",
                    "&:hover .overlay": {
                      opacity: 1,
                    },
                  }}>
                  <img
                    width="100%"
                    src={product?.featured_image}
                    alt={`Product ${index}`}
                    decoding="async"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                  <Box
                    className="overlay"
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: "rgba(0, 0, 0, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      opacity: 0,
                      transition: "opacity 0.3s ease",
                    }}>
                    <Button
                      variant="contained"
                      sx={{
                        backgroundColor: "#333333",
                        color: "#fff",
                        padding: "12px 20px",
                        borderRadius: "0px",
                        fontSize: "14px",
                        fontWeight: 400,
                        textTransform: "none",
                        "&:hover": { backgroundColor: "#333" },
                      }}>
                      View Details
                    </Button>
                  </Box>
                </Box>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: "18px",
                    lineHeight: "22px",
                    color: "#000000",
                    letterSpacing: "0%",
                    fontWeight: 500,
                    fontFamily: "Avenir Book",
                    mt: 1,
                    textAlign: "left",
                  }}>
                  {product?.name}
                </Typography>
              </Box>
            </Grid>
          ))}
          {isFetchingNextPage &&
            Array.from({ length: 4 }).map((_, i) => (
              <Grid
                item
                key={`skeleton-next-${i}`}
                xs={12}
                sm={6}
                md={3}
                sx={{ marginBottom: "20px" }}>
                <ProductCardSkeleton />
              </Grid>
            ))}
          <Grid item xs={12} sx={{ display: "flex", justifyContent: "center" }}>
            {hasNextPage && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                sx={{
                  width: "fit-content",
                  margin: "0 auto",
                  px: 4,
                  py: 1,
                  backgroundColor: "#333",
                  borderRadius: "0px",
                  color: "#fff",
                  fontFamily: "Avenir Book",
                  "&:hover": { backgroundColor: "#333" },
                }}>
                {isFetchingNextPage ? (
                  <CircularProgress size={24} />
                ) : (
                  "Load More"
                )}
              </Button>
            )}
          </Grid>
          <CustomDialog
            open={dialogOpen}
            handleClose={handleCloseDialog}
            image={selectedProduct?.featured_image}
            title={selectedProduct?.name}
          />
        </Grid>
      ) : (
        <Stack justifyContent={"center"} alignItems={"center"}>
          <Typography>No Products Found</Typography>
        </Stack>
      )}
    </>
  );
}

export default Products;
